import React, { Fragment, useEffect, useState } from 'react';
import TableCell from './table-cell/TableCell';
import { SelectOption } from '../select/Select';
import { arrayFromNToM } from '../../utils/arrayUtils';
import TableHeader from './TableHeader';

interface TableProps {
  setTableConfig: any;
  tableConfig: TableConfig;
  name?: string;
  data: Array<any>;
  setData?: any;
  defaultEmptyRow?: any;
  tabSkipIndices?: any;
  tableCellOnBlur?: any;
  edit?: boolean;
  withRowIndex?: boolean;
  selectByAllTablecells?: boolean;
  classNameSelect?: string;
  selectedRow: number | undefined;
  setSelectedRow: (value: number | undefined) => void;
  selectedRows?: number[];
  setSelectedRows?: (value: number[]) => void;
  doubleClickRowHandler?: (rowIndex: number) => void;
  setAuftraegeSortBy?: (attributeName: string, sortingCount: number) => void;
  sortingCount?: number;
  sortBy?: string;
  toggleSort?: boolean;
  searchQueryString?: string;
  lastSelectedColumnHeader?: number;
  setLastSelectedColumnHeader?: (value: number) => void;
  saveUserAnsicht?: () => void;
}

export interface ColumnConfig {
  id: number;
  columnName: string;
  attributName: string;
  width?: string;
  valueType: string;
  selectData?: SelectOption[];
  openSpecificModal?: any;
  autoCompleteFormat?: any;
  autoCompleteGetter?: any;
  hierarchicalAttributename?: string;
  required?: boolean;
  minWidth?: string;
  filterList?: boolean;
  onFocus?: any;
  filterTyp?: string;
  fixedDecimalScale?: boolean;
  readonly?: boolean;
  maxWidth?: string;
}

export type TableConfig = Array<ColumnConfig>;

/**
 * React Komponente repräsentiert eine editierbare Tabelle.
 *
 * @param props.tableConfig Tabellenkonfiguration, Liste von ColumnConfigs,
 *                    in der verschiedene Eigenschaften wie z.B. die
 *                    Überschrift der Spalte oder die Spaltenbreite
 *                    festgehalten werden
 * @param props.setTableConfig Setzung der Tabellenkonfiguration
 * @param data Tabelleninhalt
 * @param setData Setzung des Tabelleninhalts
 * @param defaultEmptyRow Standard Objekt für eine leere Reihe
 * @param tabSkipIndices Liste der Tabreihenfolge
 * @param tableCellOnBlur falls ein onBlur Effekt bei Verlassen einer
 *                        Zelle ausgelöst werden soll, kann hier eine
 *                        entsprechende Funktion übergeben werden
 * @param classNameSelect Liste der Tabreihenfolge
 * @param edit editierbare Tabelle oder nicht?
 *             @default = true
 * @param withRowIndex falls true, wird in der ersten Spalte die
 *                     Nummer der Reihe angezeigt, Start bei 1
 *                     @default = false
 * @param selectByAllTablecells falls true, wird bei anklicken einer Zelle
 *                              die ganze Spalte markiert und hervorgehoben
 * @param selectedRow selektierte bzw. zuletzt selektierte Row als Index
 * @param setSelectedRow Setter für selectedRow
 * @param selectedRows selektierte Rows als nummer[] mit den Indizes der Rows
 * @param setSelectedRows Setter für selectedRows
 * @param searchQueryString Eingabe im Suchfeld zur Markierung der Suchergebnisse
 * @default = true
 *
 * @returns {JSX.Element}
 */
const Table = (props: TableProps) => {
  const COLUMN_SIZE_ADDITION_VALUE = 30;
  const COLUMN_MIN_SIZE = 70;

  useEffect(() => {
    props.edit && setEmptyTableRows();
  }, [props.data]);

  useEffect(() => {
    props.tableConfig.forEach((config, index) => {
      if (config.minWidth != undefined) {
        setColumnWidth(Number(config.minWidth) ?? 0, index);
      } else {
        calculateColumnWidth(config.columnName, config, index);
      }
    });
  }, []);

  const [selectedDataIds, setSelectedDataId] = useState<number[]>([]);

  useEffect(() => {
    const ids: number[] = [];
    props.selectedRows?.forEach(row => {
      ids.push(props.data[row].id);
    });
    setSelectedDataId(ids);
  }, [props.toggleSort]);

  useEffect(() => {
    const selectedRows: number[] = [];
    selectedDataIds.forEach(selectedId => {
      props.data.forEach((dataEntry, index) => {
        if (dataEntry.id === selectedId) {
          selectedRows.push(index);
        }
      });
    });
    selectedRows.length > 0 && props.setSelectedRow(selectedRows[0]);
    props.setSelectedRows && props.setSelectedRows(selectedRows);
  }, [selectedDataIds]);

  /**
   * Funktionalität um eine leere Reihe in jedem Fall zu garantieren.
   * Wenn keine leere Reihe existiert, wird eine hinzugefügt.
   * Wenn 2 leere Reihen existieren, wird die letzte Reihe entfernt.
   */
  const setEmptyTableRows = () => {
    if (
      JSON.stringify(props.defaultEmptyRow) !==
        (props.data?.length &&
          JSON.stringify(props.data[props.data?.length - 1])) &&
      props.edit
    ) {
      props.setData([...props.data, { ...props.defaultEmptyRow }]);
    }
    for (let rowIndex = 0; rowIndex < props.data.length - 1; rowIndex++) {
      if (
        JSON.stringify(props.defaultEmptyRow) ===
        JSON.stringify(props.data[rowIndex])
      ) {
        deleteRow(rowIndex);
      }
    }
  };

  /**
   * Definiert die Aktion, die ausgeführt wird, wenn sich das Element verändert.
   *
   * @param targetName Key / Attributename vom Element
   * @param targetValue Wert vom Element
   * @param rowIndex Reihenindex
   * @param hierarchicalAttributeName Attributsname des Unterobjektes sofern hierarchisch
   */
  const onChangeHandlerTableCell = (
    targetName: string,
    targetValue: any,
    rowIndex: number,
    hierarchicalAttributeName?: string
  ) => {
    const newData = [...props.data];
    //TODO: SuchText-Component sollte ein {id: number, value: string} beinhalten, damit die Artikel-Ausnahme unnötig wird
    if (!hierarchicalAttributeName) {
      if (targetName === 'artikel')
        targetValue = { id: undefined, bezeichnung: targetValue };
      newData[rowIndex][targetName] = targetValue;
    } else {
      newData[rowIndex][targetName][hierarchicalAttributeName] = targetValue;
    }
    props.setData(newData);
    props.edit && setEmptyTableRows();
  };

  /**
   * Definiert die Aktion, die ausgeführt wird, wenn das Element angeklickt wurde.
   *
   * @param rowIndex Reihenindex
   */
  const onClickHandlerTableCell = (rowIndex: number) => {
    props.setSelectedRow(rowIndex);
  };

  const onClickHandlerRows = (
    rowIndex: number,
    mouseEvent: React.MouseEvent
  ) => {
    // die selektiere Reihe für single actions,
    // z.B. Auftrag bearbeiten, kopieren
    props.setSelectedRow(rowIndex);
    if (props.selectedRows && props.setSelectedRows) {
      /*  STRG gedrueckt?
          Ja:   fuege die gerade geklickte Reihe hinzu
          Nein: SHIFT gedrueckt?
       */
      if (mouseEvent.ctrlKey && !mouseEvent.shiftKey) {
        props.selectedRows.includes(rowIndex)
          ? props.setSelectedRows(
              props.selectedRows.filter(row => row !== rowIndex)
            )
          : props.setSelectedRows([...props.selectedRows, rowIndex]);
      } else if (mouseEvent.shiftKey) {
        /*
          SHIFT ist gedrueckt.
          Sind schon Reihen markiert (length > 0)?
          Ja:    fuege alle Reihen zwischen der am naechsten gelegenen,
                 schon markierten Reihe und der gerade geklickten
                 Reihe hinzu

          Nein:  markiere die gerade angeklickte Reihe
         */
        props.selectedRows.length > 0
          ? props.setSelectedRows([
              ...props.selectedRows,
              ...arrayFromNToM(props.selectedRow || 0, rowIndex),
            ])
          : props.setSelectedRows([rowIndex]);
      } else {
        props.setSelectedRows([rowIndex]);
      }
    }
  };

  const onDoubleClickHandlerRow = (rowIndex: number) => {
    if (rowIndex !== undefined && props.doubleClickRowHandler) {
      props.setSelectedRow(rowIndex);
      props.doubleClickRowHandler(rowIndex);
    }
  };

  /**
   * Definiert die Aktion, die ausgeführt wird, wenn in dem Element "Entf"
   * gedrückt wird.
   *
   * @param key gedrückte Taste
   * @param rowIndex Reihenindex
   */
  const onKeyDownHandlerTableCell = (key: string, rowIndex: number) => {
    if (key === 'Delete' && props.selectedRow === rowIndex) {
      deleteRow(rowIndex);
      props.setSelectedRow(undefined);
    }
  };

  /**
   * Löscht eine Tabellenreihe nach mitgeliefertem Reihenindex.
   *
   * @param rowIndex Reihenindex
   */
  const deleteRow = (rowIndex: number) => {
    const newData = [...props.data];
    newData.splice(rowIndex, 1);
    props.setData([...newData]);
  };

  /**
   * Dient zur Kalkulation der Spaltenbreiten.
   *
   * @param value Wert der TableCell
   * @param column Element der ColumnConfigListe
   * @param index Index des Elementes der Column
   */
  const calculateColumnWidth = (
    value: string,
    column: ColumnConfig,
    index: number
  ) => {
    //Maxwidth
    if (column.maxWidth) {
      setColumnWidth(Number(column.maxWidth), index);
      return;
    }
    const columnName = column.columnName.toString();

    const columnNameWidth = calculateStringLengthIntoPixels(columnName);
    const greatestCalculation = calculateGreatestTdValueWitdh(column);

    const greatestWidth =
      columnNameWidth < greatestCalculation
        ? greatestCalculation
        : columnNameWidth;

    if ((column.minWidth ?? -1) < greatestWidth) {
      setColumnWidth(greatestWidth, index);
    } else {
      setColumnWidth(+(column.minWidth ?? -1), index);
    }
  };

  /**
   * Ermittelt für alle TableCells einer Spalte den längsten Wert und kalkuliert die Pixelbreite
   * @param column Columnelemten aus der ColumnConfig
   * @return number Pixelbreite
   */
  const calculateGreatestTdValueWitdh = (column: ColumnConfig): number => {
    let columnValue;
    let calculatedWidth;
    let greatestCalculation = 0;

    props.data.forEach(row => {
      columnValue = !!row[column.attributName]
        ? row[column.attributName].toString()
        : '';

      calculatedWidth = calculateStringLengthIntoPixels(columnValue);
      if (calculatedWidth > greatestCalculation) {
        greatestCalculation = calculatedWidth;
      }
    });
    return greatestCalculation;
  };

  /**
   * Setzt die Breite der ganzen Spalte mit der Beachtung der minimalen Spaltenbreite.
   * @param width Pixelbreite
   * @param index Index der Column
   */
  const setColumnWidth = (width: number, index: number) => {
    const tempColumnConfig = [...props.tableConfig];
    if (width < COLUMN_MIN_SIZE) {
      width = COLUMN_MIN_SIZE;
    }
    tempColumnConfig[index].width = width + 'px';
    props.setTableConfig(tempColumnConfig);
  };

  /**
   * Kalkuliert die Pixelbreite von einem Stringwert mit dem maximalen StringLength Wert von 40.
   * @param value Stringwert
   * @return number Pixel
   */
  const calculateStringLengthIntoPixels = (value: string): number => {
    if (value.length <= 40) {
      const calculation = value.length * 9 + COLUMN_SIZE_ADDITION_VALUE;
      return calculation < COLUMN_MIN_SIZE ? COLUMN_MIN_SIZE : calculation;
    } else {
      return 40 * 9 + COLUMN_SIZE_ADDITION_VALUE;
    }
  };

  const onBlurHandler = (
    value: any,
    column: ColumnConfig,
    columnIndex: number,
    rowIndex: number,
    e: any
  ) => {
    if (props.tableCellOnBlur) {
      props.tableCellOnBlur(rowIndex, column.attributName);
    }

    const stringValue = value ? value.toString() : '';
    calculateColumnWidth(stringValue, column, columnIndex);
  };

  return (
    <Fragment>
      <table id={'table'}>
        {/* Table Header
         *   Ist 'withRowIndex' = true, wird in der ersten Spalte der Index
         *   der Reihe angezeigt
         *
         *   Alle anderen Spalten werden aus der props.tableConfig übernommen.
         */}
        <thead>
          <tr>
            {props.withRowIndex && <th style={{ width: 50 }}>Nr.</th>}

            {props.tableConfig.map((value: ColumnConfig, index: number) => (
              // inline style, um die Spaltenbreite dynamisch ändern zu können
              <TableHeader
                key={index}
                value={value}
                setAuftraegeSortBy={props.setAuftraegeSortBy}
                sortingCount={props.sortingCount}
                sortBy={props.sortBy}
                lastSelectedColumnHeader={props.lastSelectedColumnHeader}
                setLastSelectedColumnHeader={props.setLastSelectedColumnHeader}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {/*  Jeder Eintrag in data wird über eine Reihe
           *   in der Tabelle repräsentiert, data kann z.B.
           *   eine Liste von Aufträgen sein, muss aber in
           *   jedem Fall eine Liste von Objects sein.
           *   Welche Attribute der Objects wie angezeigt werden,
           *   wird in der props.tableConfig festgelegt.
           */}
          {props.data &&
            props.data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={`${
                  props.selectedRow === rowIndex ||
                  props.selectedRows?.includes(rowIndex)
                    ? 'row-selected'
                    : ''
                }`}
                onKeyDown={event =>
                  onKeyDownHandlerTableCell(event.key, rowIndex)
                }
                onClick={
                  props.selectByAllTablecells
                    ? e => onClickHandlerRows(rowIndex, e)
                    : undefined
                }
                onDoubleClick={
                  props.selectedRow === rowIndex
                    ? () => onDoubleClickHandlerRow(rowIndex)
                    : undefined
                }
              >
                {/* Ist 'withRowIndex' = true, wird in der ersten
                 *  Spalte der Index der Reihe angezeigt, siehe Header
                 */}
                {props.withRowIndex && (
                  <TableCell
                    name='Nr'
                    key={rowIndex}
                    value={rowIndex + 1}
                    rowIndex={rowIndex}
                    valueType={'button'}
                    onClickHandler={onClickHandlerTableCell}
                  />
                )}
                {/* alle anderen Spalten werden, wie im Header,
                 *  aus der props.tableConfig übernommen
                 */}
                {props.tableConfig &&
                  props.tableConfig.map(
                    (column: ColumnConfig, indexColumn: number) => {
                      return Object.entries(item).map(
                        ([key, value], index: number) => {
                          if (column.attributName === key) {
                            if (props.edit) {
                              return (
                                <TableCell
                                  name={key}
                                  key={index}
                                  tableName={props.name ?? ''}
                                  value={value}
                                  rowIndex={rowIndex}
                                  onChangeHandler={onChangeHandlerTableCell}
                                  onClickHandler={onClickHandlerTableCell}
                                  tabIndex={
                                    props.tabSkipIndices.includes(index - 1)
                                      ? -1
                                      : 0
                                  }
                                  valueType={
                                    props.tableConfig[indexColumn].valueType
                                  }
                                  onBlurHandler={(e: any) =>
                                    onBlurHandler(
                                      value,
                                      column,
                                      indexColumn,
                                      rowIndex,
                                      e
                                    )
                                  }
                                  onFocusHandler={
                                    props.tableConfig[indexColumn].onFocus
                                  }
                                  edit={
                                    !props.tableConfig[indexColumn].readonly &&
                                    props.edit
                                  }
                                  selectByAllTablecells={
                                    props.selectByAllTablecells
                                  }
                                  selectData={column.selectData}
                                  classNameSelect={props.classNameSelect}
                                  specificModal={
                                    props.tableConfig[indexColumn]
                                      .openSpecificModal
                                  }
                                  fixedDecimalScale={
                                    props.tableConfig[indexColumn]
                                      .fixedDecimalScale
                                  }
                                  AutoCompleteFormat={
                                    props.tableConfig[indexColumn]
                                      .autoCompleteFormat
                                  }
                                  autoCompleteGetter={
                                    props.tableConfig[indexColumn]
                                      .autoCompleteGetter
                                  }
                                  hierarchicalAttributename={
                                    props.tableConfig[indexColumn]
                                      .hierarchicalAttributename
                                  }
                                  required={
                                    rowIndex + 1 != props.data.length ||
                                    props.data.length == 1
                                      ? props.tableConfig[indexColumn].required
                                      : false
                                  }
                                  filterList={
                                    props.tableConfig[indexColumn].filterList
                                  }
                                  filterTyp={
                                    props.tableConfig[indexColumn].filterTyp
                                  }
                                  searchQueryString={props.searchQueryString}
                                />
                              );
                            } else {
                              return (
                                <TableCell
                                  name={key}
                                  key={index}
                                  value={value}
                                  onClickHandler={onClickHandlerTableCell}
                                  onBlurHandler={(e: any) =>
                                    onBlurHandler(
                                      value,
                                      column,
                                      indexColumn,
                                      rowIndex,
                                      e
                                    )
                                  }
                                  valueType={
                                    props.tableConfig[indexColumn].valueType
                                  }
                                  fixedDecimalScale={
                                    props.tableConfig[indexColumn]
                                      .fixedDecimalScale
                                  }
                                  rowIndex={rowIndex}
                                  edit={props.edit}
                                  selectByAllTablecells={
                                    props.selectByAllTablecells
                                  }
                                  filterList={
                                    props.tableConfig[indexColumn].filterList
                                  }
                                  searchQueryString={props.searchQueryString}
                                />
                              );
                            }
                          }
                        }
                      );
                    }
                  )}
              </tr>
            ))}
        </tbody>
      </table>
    </Fragment>
  );
};

Table.defaultProps = {
  edit: true,
  withRowIndex: false,
  selectByAllTablecells: true,
};

export default Table;
